import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowUpDown, 
  ChevronDown, 
  Info, 
  Clock, 
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Wallet,
  Zap,
  X,
  TrendingUp,
  DollarSign,
  Lock
} from 'lucide-react';
import { useExchangeForm, useAddressValidation, useCreateTransaction } from '../hooks/useExchange';
import { useAuth } from '../context/AuthContext';
import CurrencySelector from './CurrencySelector';
import CurrencyIcon from './CurrencyIcon';
import { getIconUrl } from '../services/iconService';
import { fetchTokenPrice } from '../services/api';
import './ExchangeWidget.css';

// Brand colors for popular coins
const coinColors = {
  btc: '#f7931a',
  eth: '#627eea',
  usdt: '#26a17b',
  bnb: '#f3ba2f',
  sol: '#9945ff',
  ada: '#0033ad',
  xrp: '#23292f',
  dot: '#e6007a',
  doge: '#c2a633',
  matic: '#8247e5',
  avax: '#e84142',
  ltc: '#bfbbbb',
  link: '#2a5ada',
  uni: '#ff007a',
  atom: '#2e3148',
  etc: '#328332',
  xlm: '#08b5e5',
  trx: '#ff060a',
};

const CurrencyIconWithFallback = ({ currency, size = 'small' }) => {
  const [error, setError] = useState(false);
  const color = coinColors[currency?.ticker?.toLowerCase()] || '#3b82f6';
  const initial = currency?.ticker?.charAt(0).toUpperCase() || '?';
  const className = size === 'large' ? 'currency-icon-large' : 'currency-icon';
  
  if (error) {
    return (
      <div 
        className={className}
        style={{ backgroundColor: color }}
      >
        {initial}
      </div>
    );
  }
  
  return (
    <CurrencyIcon
      currency={currency}
      size={size === 'large' ? 'lg' : 'md'}
      onError={() => setError(true)}
    />
  );
};

const ExchangeWidget = ({ variant = 'default', embedded = false }) => {
  const [showCurrencySelector, setShowCurrencySelector] = useState(null);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showExtraId, setShowExtraId] = useState(false);
  const [showArbitrageModal, setShowArbitrageModal] = useState(false);
  const [pendingArbitrage, setPendingArbitrage] = useState(false);
  const [arbitrageError, setArbitrageError] = useState(null);
  const [fromTokenPrice, setFromTokenPrice] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const { isAuthenticated } = useAuth();

  const {
    fromCurrency,
    toCurrency,
    fromAmount,
    toAmount,
    recipientAddress,
    recipientExtraId,
    refundAddress,
    refundExtraId,
    arbitrageEnabled,
    setFromCurrency,
    setToCurrency,
    setFromAmount,
    setToAmount,
    setRecipientAddress,
    setRecipientExtraId,
    setRefundAddress,
    setRefundExtraId,
    setArbitrageEnabled,
    rateData,
    isRateLoading,
    swapCurrencies,
    estimatedTime
  } = useExchangeForm();

  const validation = useAddressValidation(
    toCurrency?.ticker,
    recipientAddress,
    recipientExtraId
  );

  const refundValidation = useAddressValidation(
    fromCurrency?.ticker,
    refundAddress,
    refundExtraId
  );

  const createTransactionMutation = useCreateTransaction();

  // Set default currencies on mount
  useEffect(() => {
    if (!fromCurrency) {
      setFromCurrency({
        id: 'bitcoin',
        ticker: 'btc',
        name: 'Bitcoin',
        image: getIconUrl('bitcoin'),
        network: 'BTC',
        hasExtraId: false,
        precision: 8,
      });
    }
    if (!toCurrency) {
      setToCurrency({
        id: 'ethereum',
        ticker: 'eth',
        name: 'Ethereum',
        image: getIconUrl('ethereum'),
        network: 'ETH',
        hasExtraId: false,
        precision: 18,
      });
    }
  }, []);

  const handleExchange = async () => {
    setError(null);

    if (!validation.isValid) {
      setError('Please enter a valid recipient address');
      return;
    }

    if (!termsAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    try {
      const result = await createTransactionMutation.mutateAsync({
        fromCurrency: fromCurrency.ticker,
        toCurrency: toCurrency.ticker,
        fromAmount,
        toAmount,
        recipientAddress,
        recipientExtraId: recipientExtraId || undefined,
        refundAddress: refundAddress || undefined,
        refundExtraId: refundExtraId || undefined,
        arbitrage: arbitrageEnabled,
      });

      // Navigate to transaction status
      window.location.href = `/tx/${result.id}`;
    } catch (err) {
      setError(err.message || 'Failed to create exchange. Please try again.');
    }
  };

  // Fetch token price for USD calculation
  useEffect(() => {
    const getTokenPrice = async () => {
      if (fromCurrency?.id) {
        try {
          const priceData = await fetchTokenPrice(fromCurrency.id);
          setFromTokenPrice(priceData?.price || null);
        } catch (err) {
          setFromTokenPrice(null);
        }
      }
    };
    getTokenPrice();
  }, [fromCurrency]);

  // Calculate USD value of input
  const getInputUsdValue = () => {
    if (!fromAmount || !fromTokenPrice || parseFloat(fromAmount) <= 0) {
      return 0;
    }
    return parseFloat(fromAmount) * fromTokenPrice;
  };

  const ARBITRAGE_MIN_USD = 500;

  const handleArbitrageToggle = async () => {
    setArbitrageError(null);
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    
    if (!arbitrageEnabled) {
      // Turning on - check threshold first
      const usdValue = getInputUsdValue();
      
      if (usdValue < ARBITRAGE_MIN_USD) {
        setArbitrageError(
          `Input value must be at least $${ARBITRAGE_MIN_USD.toLocaleString()} USD for Advanced Trading to be activated. Current value: ~$${Math.floor(usdValue).toLocaleString()} USD`
        );
        return;
      }
      
      // Show confirmation modal if threshold is met
      setPendingArbitrage(true);
      setShowArbitrageModal(true);
    } else {
      // Turning off - disable immediately
      setArbitrageEnabled(false);
      setArbitrageError(null);
    }
  };

  const confirmArbitrage = () => {
    setArbitrageEnabled(true);
    setShowArbitrageModal(false);
    setPendingArbitrage(false);
    setArbitrageError(null);
  };

  const cancelArbitrage = () => {
    setShowArbitrageModal(false);
    setPendingArbitrage(false);
  };

  // Clear arbitrage error when amount changes
  useEffect(() => {
    if (arbitrageError) {
      setArbitrageError(null);
    }
  }, [fromAmount, fromCurrency]);

  // Auto-disable arbitrage if value drops below threshold
  useEffect(() => {
    if (arbitrageEnabled) {
      const usdValue = getInputUsdValue();
      if (usdValue < ARBITRAGE_MIN_USD) {
        setArbitrageEnabled(false);
        setArbitrageError(
          `Advanced Trading has been deactivated because the input value dropped below $${ARBITRAGE_MIN_USD.toLocaleString()} USD.`
        );
      }
    }
  }, [fromAmount, fromTokenPrice, arbitrageEnabled]);

  const handleCurrencySelect = (currency) => {
    if (showCurrencySelector === 'from') {
      setFromCurrency(currency);
    } else {
      setToCurrency(currency);
    }
    setShowCurrencySelector(null);
  };

  const canExchange = 
    fromCurrency && 
    toCurrency && 
    parseFloat(fromAmount) > 0 && 
    validation.isValid && 
    termsAccepted &&
    !createTransactionMutation.isLoading &&
    (!refundAddress || refundValidation.isValid);

  const minAmount = fromCurrency ? (0.001).toFixed(fromCurrency.precision) : '0.001';

  return (
    <div className={`exchange-widget ${variant} ${embedded ? 'embedded' : ''}`}>
      {/* Floating Badge */}
      <div className="widget-badge">
        <Shield className="badge-icon" />
        <span>No KYC required</span>
      </div>

      <div className="widget-header">
        <h2 className="widget-title">Exchange Crypto</h2>
        <p className="widget-subtitle">Fast, limitless, and secure</p>
      </div>

      <div className="exchange-form">
        {/* You Send */}
        <div className="exchange-field">
          <label className="field-label">
            You Send
            <span className="field-hint">Min: {minAmount}</span>
          </label>
          <div className="input-group">
            <input
              type="number"
              value={fromAmount}
              onChange={(e) => setFromAmount(e.target.value)}
              placeholder="0.00"
              className="amount-input"
              min="0"
              step="any"
            />
            <button
              className="currency-btn"
              onClick={() => setShowCurrencySelector('from')}
            >
              {fromCurrency ? (
                <>
                  <CurrencyIconWithFallback currency={fromCurrency} />
                  <span className="currency-code">{fromCurrency.ticker.toUpperCase()}</span>
                  <ChevronDown className="chevron" />
                </>
              ) : (
                <span className="select-text">Select</span>
              )}
            </button>
          </div>
          {fromCurrency && (
            <span className="network-tag">{fromCurrency.network}</span>
          )}
        </div>

        {/* Swap Button */}
        <button 
          className="swap-button"
          onClick={swapCurrencies}
          disabled={!fromCurrency || !toCurrency}
          aria-label="Swap currencies"
        >
          <ArrowUpDown className="swap-icon" />
        </button>

        {/* You Get */}
        <div className="exchange-field">
          <label className="field-label">You Get</label>
          <div className="input-group">
            <input
              type="text"
              value={toAmount ? `≈ ${parseFloat(toAmount).toFixed(6)}` : ''}
              readOnly
              placeholder="0.00"
              className="amount-input readonly"
            />
            <button
              className="currency-btn"
              onClick={() => setShowCurrencySelector('to')}
            >
              {toCurrency ? (
                <>
                  <CurrencyIconWithFallback currency={toCurrency} />
                  <span className="currency-code">{toCurrency.ticker.toUpperCase()}</span>
                  <ChevronDown className="chevron" />
                </>
              ) : (
                <span className="select-text">Select</span>
              )}
            </button>
          </div>
          {toCurrency && (
            <span className="network-tag">{toCurrency.network}</span>
          )}
        </div>

        {/* Rate Info */}
        {rateData && fromAmount && parseFloat(fromAmount) > 0 && (
          <div className="rate-details">
            <div className="rate-row">
              <span className="rate-label">Rate</span>
              <span className="rate-value">
                1 {fromCurrency?.ticker.toUpperCase()} ≈ {' '}
                {rateData.rate?.toFixed(6) || '...'} {toCurrency?.ticker.toUpperCase()}
              </span>
            </div>
            <div className="rate-row">
              <span className="rate-label">Network fee</span>
              <span className="rate-value included">Included</span>
            </div>
            {estimatedTime && (
              <div className="rate-row">
                <Clock className="time-icon" />
                <span className="time-value">~{estimatedTime} minutes</span>
              </div>
            )}
          </div>
        )}

        {isRateLoading && fromAmount && (
          <div className="rate-loading">
            <Loader2 className="loading-spinner" />
            <span>Fetching best rate...</span>
          </div>
        )}

        {/* Advanced Trading Toggle */}
        <div className="arbitrage-toggle-container">
          <button 
            className={`arbitrage-toggle ${arbitrageEnabled ? 'active' : ''} ${arbitrageError ? 'error' : ''} ${!isAuthenticated ? 'disabled' : ''}`}
            onClick={handleArbitrageToggle}
            type="button"
            disabled={!isAuthenticated}
          >
            <div className="arbitrage-toggle-content">
              <div className="arbitrage-icon-wrapper">
                {isAuthenticated ? (
                  <Zap className="arbitrage-icon" />
                ) : (
                  <Lock className="arbitrage-icon" />
                )}
              </div>
              <div className="arbitrage-text">
                <span className="arbitrage-label">
                  Advanced Trading
                  {!isAuthenticated && <span className="members-only-badge">Members Only</span>}
                </span>
                <span className="arbitrage-description">
                  {arbitrageEnabled 
                    ? 'Arbitrage active - Better rates!' 
                    : 'Get 10-15% better rates via arbitrage (min $500)'}
                </span>
              </div>
            </div>
            <div className={`toggle-switch ${arbitrageEnabled ? 'active' : ''} ${!isAuthenticated ? 'disabled' : ''}`}>
              <div className="toggle-slider"></div>
            </div>
          </button>
          {arbitrageEnabled && rateData?.rate && (
            <div className="arbitrage-badge">
              <TrendingUp className="badge-icon" />
              <span>Boosted Rate Active</span>
            </div>
          )}
          {arbitrageError && (
            <div className="arbitrage-error">
              <AlertCircle className="error-icon" />
              <span>{arbitrageError}</span>
            </div>
          )}
          {!isAuthenticated && (
            <div className="arbitrage-login-notice">
              <Lock size={14} />
              <span>
                <Link to="/login" onClick={(e) => { e.stopPropagation(); }}>Sign in</Link> or <Link to="/login?tab=register" onClick={(e) => { e.stopPropagation(); }}>create an account</Link> to unlock Advanced Trading
              </span>
            </div>
          )}
        </div>

        {/* Recipient Address */}
        <div className="address-field">
          <label className="field-label">
            Recipient Address
            <span className="required">*</span>
          </label>
          <div className="address-input-wrapper">
            <Wallet className="address-icon" />
            <input
              type="text"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              placeholder={`Enter ${toCurrency?.ticker.toUpperCase() || ''} address`}
              className={`address-input ${validation.isValid === true ? 'valid' : validation.isValid === false ? 'invalid' : ''}`}
            />
            {validation.isValid === true && (
              <CheckCircle2 className="validation-icon valid" />
            )}
            {validation.isValid === false && (
              <AlertCircle className="validation-icon invalid" />
            )}
          </div>
          {validation.message && (
            <span className={`validation-message ${validation.isValid ? 'valid' : 'invalid'}`}>
              {validation.message}
            </span>
          )}

          {/* Extra ID Toggle */}
          {toCurrency?.hasExtraId && (
            <div className="extra-id-section">
              <button 
                className="extra-id-toggle"
                onClick={() => setShowExtraId(!showExtraId)}
              >
                <Info className="info-icon" />
                <span>Add {toCurrency.extraIdName || 'Memo'} (optional)</span>
              </button>
              {showExtraId && (
                <input
                  type="text"
                  value={recipientExtraId}
                  onChange={(e) => setRecipientExtraId(e.target.value)}
                  placeholder={`${toCurrency.extraIdName || 'Memo'} (optional)`}
                  className="extra-id-input"
                />
              )}
            </div>
          )}
        </div>

        {/* Refund Address */}
        <div className="refund-field">
          <button 
            className="refund-toggle"
            onClick={() => {
              const el = document.getElementById('refund-inputs');
              if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
            }}
          >
            <Info className="info-icon" />
            <span>Refund address (optional)</span>
          </button>
          <div id="refund-inputs" style={{ display: 'none' }}>
            <div className="address-input-wrapper">
              <Wallet className="address-icon" />
              <input
                type="text"
                value={refundAddress}
                onChange={(e) => setRefundAddress(e.target.value)}
                placeholder={`Refund ${fromCurrency?.ticker.toUpperCase() || ''} address`}
                className={`address-input refund-input ${refundAddress ? (refundValidation.isValid ? 'valid' : refundValidation.isValid === false ? 'invalid' : '') : ''}`}
              />
              {refundValidation.isValid === true && (
                <CheckCircle2 className="validation-icon valid" />
              )}
              {refundValidation.isValid === false && refundAddress && (
                <AlertCircle className="validation-icon invalid" />
              )}
            </div>
            {refundValidation.message && refundAddress && (
              <span className={`validation-message ${refundValidation.isValid ? 'valid' : 'invalid'}`}>
                {refundValidation.message}
              </span>
            )}
            {fromCurrency?.hasExtraId && (
              <input
                type="text"
                value={refundExtraId}
                onChange={(e) => setRefundExtraId(e.target.value)}
                placeholder={`Refund ${fromCurrency.extraIdName || 'Memo'}`}
                className="refund-input extra-id-input"
              />
            )}
          </div>
        </div>

        {/* Terms */}
        <label className="terms-label">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <span>
            I agree to the <Link to="/terms">Terms of Service</Link>,{' '}
            <Link to="/privacy">Privacy Policy</Link>, and{' '}
            <Link to="/aml-kyc">AML/KYC</Link>
          </span>
        </label>

        {/* Error */}
        {error && (
          <div className="error-alert">
            <AlertCircle className="alert-icon" />
            {error}
          </div>
        )}

        {/* Exchange Button */}
        <button
          className={`exchange-action-btn ${canExchange ? 'active' : ''}`}
          onClick={handleExchange}
          disabled={!canExchange}
        >
          {createTransactionMutation.isLoading ? (
            <>
              <Loader2 className="btn-spinner" />
              Creating Exchange...
            </>
          ) : (
            'Exchange Now'
          )}
        </button>

        {/* Trust Badges */}
        <div className="trust-badges">
          <div className="trust-badge">
            <Clock className="badge-icon" />
            <span>Avg. 2 min</span>
          </div>
          <div className="trust-badge">
            <Shield className="badge-icon" />
            <span>No KYC</span>
          </div>
          <div className="trust-badge">
            <span className="badge-text">1000+ coins</span>
          </div>
        </div>
      </div>

      {/* Currency Selector Modal */}
      {showCurrencySelector && (
        <CurrencySelector
          onSelect={handleCurrencySelect}
          onClose={() => setShowCurrencySelector(null)}
          excludeCurrency={showCurrencySelector === 'from' ? toCurrency : fromCurrency}
          type={showCurrencySelector}
        />
      )}

      {/* Arbitrage Confirmation Modal */}
      {showArbitrageModal && (
        <div className="modal-overlay" onClick={cancelArbitrage}>
          <div className="arbitrage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="arbitrage-modal-header">
              <div className="arbitrage-modal-icon">
                <Zap className="icon" />
              </div>
              <button className="modal-close-btn" onClick={cancelArbitrage}>
                <X className="icon" />
              </button>
            </div>
            <div className="arbitrage-modal-content">
              <h3>Activate Advanced Trading?</h3>
              <p className="arbitrage-modal-description">
                Advanced Trading uses arbitrage technology to search for the best prices 
                across the entire crypto market, providing you with <strong>10-15% better rates</strong> 
                on your exchanges.
              </p>
              <div className="arbitrage-features">
                <div className="arbitrage-feature">
                  <TrendingUp className="feature-icon" />
                  <span>Up to 15% more output</span>
                </div>
                <div className="arbitrage-feature">
                  <Shield className="feature-icon" />
                  <span>Market-wide price scanning</span>
                </div>
                <div className="arbitrage-feature">
                  <Clock className="feature-icon" />
                  <span>Real-time rate optimization</span>
                </div>
                <div className="arbitrage-feature">
                  <DollarSign className="feature-icon" />
                  <span>Minimum $1,000 USD required</span>
                </div>
              </div>
              <div className="arbitrage-modal-actions">
                <button className="btn-cancel" onClick={cancelArbitrage}>
                  Cancel
                </button>
                <button className="btn-confirm" onClick={confirmArbitrage}>
                  <Zap className="btn-icon" />
                  Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeWidget;
